/** @jsxImportSource @emotion/react */

import { FC, useCallback, useMemo } from 'react'
import Typography from '@mui/material/Typography'
import { Theme, useTheme } from '@emotion/react'
import { ScriptableContext, ScriptableScaleContext } from 'chart.js'
import { alpha } from '@mui/material'
import { format } from 'date-fns'
import Loader from 'components/Loader'
import { CourseName, getColorOptions } from 'theme/utils'
import { Chart } from 'components/Chart'
import { findMCATSpecificGameAnalyticsAttempts_findMCATSpecificGameAnalyticsAttempts_result as Result } from '__generatedTypes__/findMCATSpecificGameAnalyticsAttempts'
import { ChartActions } from '../ChartActions'
import * as commonClasses from '../styles'
import { TooltipContent } from '../TooltipContent'
import * as classes from './styles'
import { useChartData } from './hooks'

export const getMcatCustomDataset = (theme: Theme) => ({
  borderColor: getColorOptions(theme.colors.accent).main,
  pointBackgroundColor: '#1A1A1A',
  pointBorderColor: getColorOptions(theme.colors.accent).main,
  pointRadius: 6,
  pointBorderWidth: 4,
  pointHoverBorderColor: getColorOptions(theme.colors.accent).main,
  pointHoverRadius: 8,
  pointHoverBorderWidth: 6,
  backgroundColor: (context: ScriptableContext<'line'>) => {
    const ctx = context.chart.ctx
    const gradient = ctx.createLinearGradient(0, 0, 0, 300)
    gradient.addColorStop(0, getColorOptions(theme.colors.accent).light)
    gradient.addColorStop(1, alpha(getColorOptions(theme.colors.accent).main, 0.02))
    return gradient
  },
})

type Props = {
  subjectId: string
}

type PointData = { xAxisValue: string; yAxisValue: number; dateRaw: Date; result?: Result }

export const AttemptsAnalyticsChart: FC<Props> = ({ subjectId }) => {
  const theme = useTheme()

  const {
    chartData,
    isInitiallyLoaded: isChartInitiallyLoaded,
    isFetchingMore: isLoadingChartData,
    onPrevClick,
    onNextClick,
  } = useChartData(subjectId)

  const renderTooltipContent = useCallback(
    (pointData: PointData) => (
      <TooltipContent
        css={classes.tooltipContent}
        items={[
          { title: 'Date', value: format(pointData.dateRaw, 'MM/dd/yy') },
          { title: 'Attempts', value: `${pointData.result?.attempts}` },
          { title: 'Correct:', value: `${pointData.result?.correct}` },
          { title: 'Incorrect:', value: `${pointData.result?.incorrect}` },
        ]}
      />
    ),
    []
  )

  const chartOptions = useMemo(
    () => ({
      scales: {
        y: {
          beginAtZero: false,
          grid: {
            color: (context: ScriptableScaleContext) => {
              if (context.tick?.value === 0) {
                return theme.backgrounds.backgroundOne
              }

              return theme.backgrounds.backgroundOne
            },
          },
          ticks: {
            count: 6,
            stepSize: 1,
            callback: (tickValue: number | string) => `${Math.round(Number(tickValue || 0))}`,
          },
          suggestedMin: 0,
          suggestedMax: 5,
        },
      },
    }),
    [chartData]
  )

  return (
    <>
      <div css={commonClasses.chartCard}>
        <div css={commonClasses.cardHeading}>
          <div css={commonClasses.headingTitles}>
            <Typography variant="h5" css={commonClasses.cardTitle}>
              # Attempts
            </Typography>
          </div>

          <ChartActions onPrevClick={onPrevClick} onNextClick={onNextClick} />
        </div>

        <div css={classes.chartWrapper}>
          {(!isChartInitiallyLoaded || isLoadingChartData) && (
            <div css={classes.chartDataLoaderWrapper}>
              <Loader />
            </div>
          )}
          <Chart
            data={chartData}
            hideTooltipTriangle
            css={classes.chart}
            subject={CourseName.Biology}
            datasetOptions={getMcatCustomDataset(theme)}
            chartOptions={chartOptions}
            renderTooltipContent={renderTooltipContent}
            tooltipStyles={classes.tooltipWrapper}
          />
        </div>
      </div>
    </>
  )
}
